import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { useFeatureFlags } from '@atlassian/mpac-feature-flags';
import merge from 'lodash/fp/mergeAll';
import { useMemo, useState } from 'react';
import { ANALYTICS_CATEGORY_MARKETPLACE_APP_INSTALL, AppInstalledEvent, AppInstallFailedEvent } from '../../../../analytics/Events';
import { ANALYTICS_MODAL_STANDALONE_INSTALL, ANALYTICS_MODAL_STANDALONE_INSTALL_DETERMINING, ANALYTICS_MODAL_STANDALONE_INSTRUCTIONAL_APP_INSTALL } from '../../../../analytics/Sources';
import { foldInstallMode } from '../../../../records/InstallMode/InstallMode';
import { INSTRUCTIONAL_INSTALL_MODE } from '../../../../records/InstallMode/InstructionalInstallMode';
import { shouldInstallWithStoreBff } from '../../utils/installableApp';
export const getAppDisplayDetails = installMode => {
  const noop = () => null;
  return foldInstallMode(installMode)( /* determining */noop, /* appSpecificPassword */noop, instructionalMode => instructionalMode.getIn(['instructionalApp', 'details']), installableMode => installableMode.getIn(['installableApp', 'marketplaceAppDetails']), /* manualUrl */noop, /* upgrade */noop);
};
const makeAnalyticsContextAttributes = _ref => {
  let {
    baseData,
    installLocation
  } = _ref;
  const attributes = {
    installLocation,
    category: ANALYTICS_CATEGORY_MARKETPLACE_APP_INSTALL
  };
  if (!baseData.result) return attributes;
  const appDetails = getAppDisplayDetails(baseData.result.installMode);
  if (!appDetails) return attributes;
  const {
    key: appKey,
    paymentModel,
    ecosystemCloudAppId,
    programs
  } = appDetails;
  return _objectSpread(_objectSpread(_objectSpread({}, attributes), {}, {
    appKey,
    paymentModel,
    programs,
    isEcosystemCloudApp: !!ecosystemCloudAppId
  }, ecosystemCloudAppId && {
    ecosystemCloudAppId
  }), {}, {
    installPoweredByStoreBFF: shouldInstallWithStoreBff(baseData)
  });
};
const makeAnalyticsContextProperties = _ref2 => {
  let {
    currentScreen
  } = _ref2;
  return _objectSpread({}, currentScreen && {
    source: currentScreen.name
  });
};
export const useAppInstallAnalytics = _ref3 => {
  let {
    baseData,
    installLocation
  } = _ref3;
  const fireEvent = useAnalyticsEvents();
  const featureFlags = useFeatureFlags();
  const [currentScreen, setCurrentScreen] = useState(null);
  const analyticsContext = useMemo(() => ({
    properties: makeAnalyticsContextProperties({
      currentScreen
    }),
    attributes: makeAnalyticsContextAttributes({
      baseData,
      installLocation,
      featureFlags
    })
  }), [baseData.pending, currentScreen, installLocation]);
  const fireScreenEvent = function fireScreenEvent(eventType) {
    let payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return fireEvent(merge([{
      eventType
    }, {
      attributes: analyticsContext.attributes
    }, payload]));
  };
  const fireTrackEvent = function fireTrackEvent(eventType) {
    let payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return fireEvent(merge([{
      eventType
    }, {
      source: currentScreen,
      attributes: analyticsContext.attributes
    }, payload]));
  };
  return {
    fireEvent: {
      viewedScreen: {
        determining: () => {
          fireScreenEvent(ANALYTICS_MODAL_STANDALONE_INSTALL_DETERMINING);
          setCurrentScreen(ANALYTICS_MODAL_STANDALONE_INSTALL_DETERMINING);
        },
        awaitingUserConfirmation: () => {
          if (baseData.result && baseData.result.installMode.type === INSTRUCTIONAL_INSTALL_MODE) {
            fireScreenEvent(ANALYTICS_MODAL_STANDALONE_INSTRUCTIONAL_APP_INSTALL);
            setCurrentScreen(ANALYTICS_MODAL_STANDALONE_INSTRUCTIONAL_APP_INSTALL);
          } else {
            fireScreenEvent(ANALYTICS_MODAL_STANDALONE_INSTALL);
            setCurrentScreen(ANALYTICS_MODAL_STANDALONE_INSTALL);
          }
        }
      },
      installSucceeded: appKey => {
        fireTrackEvent(AppInstalledEvent, {
          actionSubjectId: appKey
        });
        setCurrentScreen(null);
      },
      installFailed: (appKey, error) => {
        fireTrackEvent(AppInstallFailedEvent, {
          actionSubjectId: appKey,
          attributes: {
            errorCode: error.code,
            error: error.message,
            source: error.source
          }
        });
        setCurrentScreen(null);
      }
    },
    analyticsContext
  };
};